import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { TOOL_BY_SLUG, TOOLS, FORMULAS, CTA_DEFAULT } from "../tools/registry";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import NotFound from "./NotFound";

/**
 * One template for all eight calculators: calculator, explanation, methodology,
 * example, then the sweep.
 *
 * The methodology block prints its formulas from lib/formulas.generated.json,
 * which is generated from analysis.FORMULAS. Nothing here retypes a formula, so
 * the definition a visitor reads and the one the audit uses are the same string.
 */
export default function ToolPage() {
  const { slug } = useParams();
  const tool = TOOL_BY_SLUG[slug];
  const [values, setValues] = useState({});

  const result = useMemo(() => {
    if (!tool) return null;
    try {
      return tool.compute(values);
    } catch {
      return null;
    }
  }, [tool, values]);

  if (!tool) return <NotFound />;

  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));
  const others = TOOLS.filter((t) => t.slug !== tool.slug).slice(0, 4);

  return (
    <div className="min-h-screen bg-[#e8e4df] text-[#171717]">
      <Nav />
      <main className="pt-28 md:pt-36 pb-20">
        <div className="max-w-5xl mx-auto px-5 md:px-8">

          {/* ---------------------------------------------------- header */}
          <nav className="tick mb-5 text-[#525252]">
            <Link to="/tools" className="hover:text-[#171717] transition-colors">Tools</Link>
            <span className="mx-2">/</span>
            <span className="text-[#171717]">{tool.nav}</span>
          </nav>
          <h1 className="font-serif-display text-4xl md:text-6xl leading-[1.03] mb-4"
              data-testid="tool-title">
            {tool.title}
          </h1>
          <p className="font-mono-tech text-[15px] md:text-[17px] text-[#171717] max-w-2xl mb-3">
            {tool.tagline}
          </p>
          <p className="font-mono-tech text-[13px] text-[#525252] max-w-2xl leading-relaxed">
            {tool.intro}
          </p>

          {/* ------------------------------------------------ calculator */}
          <section className="mt-10 grid md:grid-cols-[1fr_1fr] gap-6 items-start"
                   data-testid="tool-calculator">
            <div className="card-cream p-6 md:p-7">
              <div className="tick mb-4">Your figures</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                {tool.fields.map((f) => (
                  <label key={f.key} className="block">
                    <span className="font-mono-tech text-[12px] text-[#525252] leading-snug block mb-1">
                      {f.label}
                      {f.optional && <span className="text-[#8a8178]"> · optional</span>}
                    </span>
                    {f.type === "choice" ? (
                      <select
                        value={values[f.key] ?? f.default}
                        onChange={set(f.key)}
                        data-testid={`field-${f.key}`}
                        className="w-full px-3 py-2 font-mono-tech text-[14px] border border-[#d6d0c7] bg-white outline-none focus:border-[#171717] transition-colors"
                      >
                        {f.options.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="flex items-center border border-[#d6d0c7] bg-white focus-within:border-[#171717] transition-colors">
                        <input
                          type="number"
                          inputMode="decimal"
                          step="any"
                          value={values[f.key] ?? ""}
                          onChange={set(f.key)}
                          placeholder={f.placeholder}
                          data-testid={`field-${f.key}`}
                          className="w-full px-3 py-2 font-mono-tech text-[14px] bg-transparent outline-none"
                        />
                        <span className="px-3 font-mono-tech text-[11px] text-[#8a8178] shrink-0">
                          {f.unit}
                        </span>
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* ------------------------------------------------- result */}
            <div className="card-cream p-6 md:p-7" data-testid="tool-result">
              {!result && (
                <p className="font-mono-tech text-[13px] text-[#525252]">
                  Enter your figures and the result appears here.
                </p>
              )}
              {result && result.unavailable && (
                <>
                  <div className="tick mb-3 text-[#9a3412]">Not computed</div>
                  <p className="font-mono-tech text-[13px] leading-relaxed">{result.unavailable}</p>
                </>
              )}
              {result && !result.unavailable && (
                <>
                  <div className="tick mb-2">{result.headline.label}</div>
                  <div className="font-serif-display text-4xl md:text-5xl leading-none mb-1"
                       data-testid="tool-headline">
                    {result.headline.value}
                  </div>
                  <div className="font-mono-tech text-[12px] text-[#525252] mb-5">
                    {result.headline.sub}
                  </div>

                  {result.marks && (
                    <div className="grid grid-cols-3 gap-2 mb-5">
                      {result.marks.map(([k, v, p]) => (
                        <div key={k} className="border border-[#ddd8ce] p-2">
                          <div className="font-mono-tech text-[10px] text-[#525252]">{k}</div>
                          <div className="font-mono-tech text-[12px] text-[#171717]">{v}</div>
                          <div className="font-mono-tech text-[10px] text-[#9a3412]">{p}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <table className="w-full font-mono-tech text-[12.5px]">
                    <tbody>
                      {result.rows.map(([k, v], i) => (
                        <tr key={i} className="border-t border-[#ddd8ce]">
                          <td className="py-1.5 pr-3 text-[#525252]">{k}</td>
                          <td className="py-1.5 text-right text-[#171717] whitespace-nowrap">{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {result.verdict && (
                    <p className="font-mono-tech text-[12.5px] leading-relaxed mt-4 pt-4 border-t border-[#ddd8ce]">
                      {result.verdict}
                    </p>
                  )}

                  {/* A skipped line is declared, never zero-filled. */}
                  {result.gaps && result.gaps.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[#ddd8ce]" data-testid="tool-gaps">
                      <div className="tick mb-2 text-[#9a3412]">What was left out</div>
                      <ul className="space-y-1.5">
                        {result.gaps.map((g, i) => (
                          <li key={i} className="font-mono-tech text-[11.5px] text-[#525252] leading-relaxed">
                            {g}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* ----------------------------------------------- explanation */}
          <section className="mt-16 max-w-3xl" data-testid="tool-explanation">
            <div className="tick mb-4">What this actually measures</div>
            {tool.explanation.map((p, i) => (
              <p key={i} className="font-mono-tech text-[14px] leading-[1.75] text-[#171717] mb-4">
                {p}
              </p>
            ))}
          </section>

          {/* ----------------------------------------------- methodology */}
          <section className="mt-14 max-w-3xl" data-testid="tool-methodology">
            <div className="tick mb-4">Method</div>
            {tool.formulaKeys.length > 0 && (
              <dl className="border-t border-[#c9c3b8] mb-5">
                {tool.formulaKeys.map((k) => {
                  const f = FORMULAS[k];
                  if (!f) return null;
                  return (
                    <div key={k} className="border-b border-[#ddd8ce] py-3">
                      <dt className="font-mono-tech text-[13px] text-[#171717] mb-1">{k}</dt>
                      <dd className="font-mono-tech text-[12.5px] text-[#525252] leading-relaxed">
                        {f.means}
                        <span className="block mt-1 text-[#9a3412]">{f.formula}</span>
                      </dd>
                    </div>
                  );
                })}
              </dl>
            )}
            {tool.limits && (
              <p className="font-mono-tech text-[12.5px] text-[#525252] leading-relaxed">
                {tool.limits}
              </p>
            )}
          </section>

          {/* --------------------------------------------------- example */}
          <section className="mt-14 max-w-3xl" data-testid="tool-example">
            <div className="tick mb-4">Worked example</div>
            <div className="card-cream p-6">
              <h3 className="font-serif-display text-2xl leading-tight mb-3">{tool.example.title}</h3>
              <p className="font-mono-tech text-[13.5px] leading-[1.75] text-[#171717]">
                {tool.example.body}
              </p>
            </div>
          </section>

          {/* ------------------------------------------------------- CTA */}
          <section className="mt-14 border-t-2 border-[#171717] pt-8" data-testid="tool-cta">
            <div className="md:flex items-end justify-between gap-8">
              <div className="max-w-2xl">
                <div className="tick mb-3">Next</div>
                <p className="font-serif-display text-2xl md:text-3xl leading-snug mb-3">
                  The sweep reads what a store publishes. The audit reads the business.
                </p>
                <p className="font-mono-tech text-[13px] text-[#525252] leading-relaxed">
                  {CTA_DEFAULT.line}
                </p>
              </div>
              <Link
                to="/#product"
                data-testid="tool-cta-button"
                className="mt-6 md:mt-0 inline-block shrink-0 bg-[#171717] text-[#f5f2ed] font-mono-tech text-[13px] px-6 py-3 hover:bg-[#431407] transition-colors"
              >
                {CTA_DEFAULT.button}
              </Link>
            </div>
          </section>

          {/* ------------------------------------------------ other tools */}
          <section className="mt-16">
            <div className="tick mb-4">Other calculators</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {others.map((t) => (
                <Link key={t.slug} to={`/tools/${t.slug}`}
                      className="card-cream p-4 hover:border-[#171717] border border-transparent transition-colors">
                  <div className="font-mono-tech text-[13px] text-[#171717] mb-1">{t.nav}</div>
                  <div className="font-mono-tech text-[11.5px] text-[#525252] leading-snug">{t.tagline}</div>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
